'use client'

/**
 * Fokus-Status im Header
 * ======================
 * Die Uhr im Header ist der EINZIGE Zugang zum Fokus-Timer. Frueher schwebte
 * der Timer als feste Karte ueber jeder Ticketseite — nicht schliessbar,
 * nicht wegzuschieben —, und die Uhr sprang stattdessen auf /tickets, was
 * niemand erwartet hat. Jetzt gilt: Klick auf die Uhr klappt den Timer auf,
 * Klick daneben / Escape / das X schliesst ihn wieder.
 */

import React, { useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useFocus } from '@/providers/focus-provider'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import PomodoroTimer from '@/app/(protected)/tickets/components/PomodoroTimer'
import { Clock, X } from 'lucide-react'

export default function FocusStatusBadge() {
  const pathname = usePathname()
  const { t } = useTranslation()
  const { running, timeLeftSec, stopCycle } = useFocus()
  const [offen, setOffen] = useState(false)

  const timeText = useMemo(() => formatTime(running ? timeLeftSec : 0), [running, timeLeftSec])

  // Steht man auf einem Ticket, soll der Timer auf dieses Ticket buchen.
  // Sonst laeuft er ohne Ticketbezug — der Timer ist nicht an Tickets gebunden.
  const ticketId = useMemo(() => {
    const treffer = /^\/tickets\/([^/]+)/.exec(pathname || '')
    return treffer ? treffer[1] : undefined
  }, [pathname])

  // Der Zeitwert wird ANGEHAENGT, nicht in den Standardtext hineingebaut —
  // sonst landete eine zufaellige Uhrzeit als deutscher Basistext im Katalog.
  const title = running
    ? `${t('focus.badge.active', { editable: false, defaultValue: 'Fokus aktiv – Rest' })}: ${timeText}`
    : t('focus.badge.idle', { editable: false, defaultValue: 'Fokuslauf öffnen' })

  return (
    <div className="flex items-center gap-2">
      <Popover open={offen} onOpenChange={setOffen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            mode="icon"
            shape="circle"
            className={`size-9 hover:bg-primary/10 hover:[&_svg]:text-primary ${running ? 'ring-2 ring-red-300' : ''}`}
            title={title}
            aria-label={title}
          >
            <div className="relative">
              <Clock className={`size-4.5! ${running ? 'text-red-500' : ''}`} />
              {running && (
                <span className="absolute -top-0.5 -right-0.5 block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              )}
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-80 max-h-[80vh] overflow-y-auto p-4"
        >
          <div className="mb-2 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground"
              onClick={() => setOffen(false)}
              title={t('common.close', { editable: false, defaultValue: 'Schließen' })}
              aria-label={t('common.close', { editable: false, defaultValue: 'Schließen' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <PomodoroTimer embedded ticketId={ticketId} />
        </PopoverContent>
      </Popover>

      <div className="hidden xl:flex items-center gap-2 text-xs text-muted-foreground">
        {running ? (
          <>
            <span className="font-semibold text-foreground">{timeText}</span>
            <button
              className="px-2 py-0.5 rounded border text-foreground/80 hover:bg-muted/40"
              onClick={stopCycle}
              title={t('focus.badge.stop', { editable: false, defaultValue: 'Fokuslauf stoppen' })}
            >
              {t('focus.badge.stop_short', { editable: false, defaultValue: 'Stop' })}
            </button>
          </>
        ) : (
          <span>{t('focus.badge.off', { editable: false, defaultValue: 'Fokus aus' })}</span>
        )}
      </div>
    </div>
  )
}

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
